import { pick } from 'js/utils/ramda.util';
import React, { useMemo, useCallback } from 'react';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { REPORT_TYPES } from '../constants';
import { useDynamicReducerSelector } from '../store';
import { DownloadLink } from '../components/DownloadLink';

import style from '../W2GReportingForm.scss';

export const useTableSummary = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const { from, until, winLoss: { currencyCode, amount: winLossAmount = 0 } } = useDynamicReducerSelector(
        pick(['from', 'until', 'winLoss'])
    );
    const amount = Number(winLossAmount) ? formatter.formatCurrency(winLossAmount, { currencyCode }) : null;
    const dates = [
        translate('summary.between'),
        formatter.date(from),
        translate('summary.and'),
        formatter.date(until)
    ].join(' ');
    const downloadLink = useMemo(() => (
        <DownloadLink
            translate={translate}
            reportType={REPORT_TYPES.WIN_LOSS}
        />
    ), [translate]);

    const getAccordionCell = useCallback((name, value) => (
        <>
            <div className="accordionBodyCellName">{name}</div>
            <div className="accordionBodyCellValue">{value}</div>
        </>
    ), []);

    return useMemo(() => {
        if (!amount) {
            return null;
        }

        return [{
            id: 0,
            isExpandedByDefault: true,
            tableView: {
                headerCells: [
                    { id: 0, content: translate('summary.title'), className: style.dates },
                    { id: 1, content: translate('summary.total') },
                    { id: 2, content: translate('pdf'), className: style.pdf }
                ],
                rowCells: [
                    { id: 0, content: dates, className: `${style.dates} ${style.highlight}` },
                    { id: 1, content: amount },
                    { id: 2, content: downloadLink, className: style.pdf }
                ]
            },
            accordionView: {
                headerCells: [{
                    id: 0,
                    content: (
                        <>
                            <span className={style.highlight}>{translate('summary.title')}</span>
                            <span>{dates}</span>
                        </>
                    ),
                    className: 'accordionHeaderSmallDetails'
                }],
                rowCells: [
                    { id: 0, content: getAccordionCell(translate('summary.total'), amount) },
                    { id: 2, content: getAccordionCell(translate('pdf'), downloadLink) }
                ]
            }
        }];
    }, [translate, amount, dates, downloadLink, getAccordionCell]);
};
